import { selectedPlaybookSchema } from "@repo/app-types/agents/selectedPlaybook";
import { configurationTestResultIdSchema } from "@repo/app-types/entities/configurationTestResult";
import type {
  CopilotProposalStatus,
  CopilotProposalSummary,
} from "@repo/app-types/entities/copilotProposal";
import type { UIMessage } from "ai";
import { z } from "zod";

/** How the AI SDK prefixes a statically-declared tool part's `type`. */
const TOOL_PART_TYPE_PREFIX = "tool-";

/**
 * A proposal as a turn shows it. `superseded` is absent by construction: that
 * proposal belongs to the later turn that replaced it, so the row a reader
 * sees is always one they can act on or have already decided.
 */
export type TurnProposal = CopilotProposalSummary & {
  status: Exclude<CopilotProposalStatus, "superseded">;
};

/**
 * The proposals one assistant turn put up for review, in the order the agent
 * made them.
 *
 * Each turn lists what it proposed rather than everything outstanding, which
 * is what keeps the transcript readable as a history: a proposal appears once,
 * where it was made, and its row carries whatever became of it. A turn that
 * proposed nothing — no proposal calls, or only tests that measured a node
 * without changing it — gets no list.
 */
export function selectTurnProposals(
  message: UIMessage,
  proposalsById: ReadonlyMap<string, CopilotProposalSummary>,
): readonly TurnProposal[] {
  if (message.role !== "assistant") {
    return [];
  }

  // A superseded proposal belongs to the later turn that replaced it, so it
  // never shows here. That also collapses a turn that revised its own
  // proposal: only the survivor gets a row.
  const byResourceKey = new Map<string, TurnProposal>();
  for (const proposalId of readProposalIds(message)) {
    const proposal = proposalsById.get(proposalId);
    if (!proposal || proposal.status === "superseded") {
      continue;
    }
    const turnProposal: TurnProposal = { ...proposal, status: proposal.status };
    // One row per target; the last one the turn made is where it ended up.
    byResourceKey.set(proposal.resource_key, turnProposal);
  }
  return [...byResourceKey.values()];
}

/**
 * The saved test results a test tool returns: enough to label the row, open
 * it in the editor, and tell whether the playbook has changed since. Read off
 * the completed call's output — unlike a proposal, a test result has no live
 * status to look up.
 */
const turnTestResultSchema = z.object({
  test_result_id: configurationTestResultIdSchema,
  playbook: selectedPlaybookSchema,
  playbook_revision_id: z.string(),
  node_name: z.string(),
});

export type TurnTestResult = z.infer<typeof turnTestResultSchema>;

/**
 * The test results one assistant turn saved, in the order it ran them. A
 * proposal's output carries no `test_result_id`, so proposals never show
 * here.
 */
export function selectTurnTestResults(
  message: UIMessage,
): readonly TurnTestResult[] {
  if (message.role !== "assistant") {
    return [];
  }
  const results: TurnTestResult[] = [];
  for (const part of message.parts) {
    if (!isCompletedToolPart(part)) {
      continue;
    }
    const parsed = turnTestResultSchema.safeParse(
      "output" in part ? part.output : undefined,
    );
    if (parsed.success) {
      results.push(parsed.data);
    }
  }
  return results;
}

/**
 * How many proposals the thread's turns have made. The agent writes proposals
 * server-side mid-turn, which no client mutation observes, so the chat watches
 * this count to know when its copy of them is stale.
 */
export function countTurnProposals(messages: readonly UIMessage[]): number {
  let total = 0;
  for (const message of messages) {
    if (message.role === "assistant") {
      total += readProposalIds(message).length;
    }
  }
  return total;
}

/**
 * Whether a message part is a finished tool call. Tool parts are typed either
 * as `dynamic-tool` or as `tool-${name}`, and both carry `state`.
 */
function isCompletedToolPart(part: UIMessage["parts"][number]): boolean {
  const isTool =
    part.type.startsWith(TOOL_PART_TYPE_PREFIX) || part.type === "dynamic-tool";
  return isTool && "state" in part && part.state === "output-available";
}

/**
 * The `proposal_id`s a turn's proposal tools returned. A proposal is recognized
 * by the shape of a completed call's output — a string `proposal_id`, which no
 * other tool returns — rather than by the tool's name, so the list keeps
 * working across tool renames and for any future proposal-writing tool. A
 * proposal is persisted only for a real change, so every returned `proposal_id`
 * is a suggestion to show.
 */
function readProposalIds(message: UIMessage): readonly string[] {
  const ids: string[] = [];
  for (const part of message.parts) {
    if (!isCompletedToolPart(part)) {
      continue;
    }
    const output = ("output" in part ? part.output : undefined) as
      | { proposal_id?: unknown }
      | undefined;
    if (typeof output?.proposal_id === "string") {
      ids.push(output.proposal_id);
    }
  }
  return ids;
}
